import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import type { TypedThunk } from '..'
import { postDelayNotificationHandled } from '../actions/delayNotification'
import type { DelayNotificationStep } from '../components/notification/DelayNotificationBar'
import { apiGET } from '../lib/api'
import type { Shift } from '../types'
import { selectDelayNotificationsEnabled } from './configurationsSlice'
import { buildTaskDelayVM } from './delayNotificationViewModel'

export type FetchStatus = 'idle' | 'pending' | 'fulfilled' | 'failed'
export type PreviousDelayKind = 'deferredNearShiftEnd' | 'unhandledPreviousShift'
export type DelayNotificationResolution = 'AMENDMENT' | 'FAULT_REPORTED'

// Unhandled delay notification from a previous shift, resolved by the backend
export interface PendingDelayNotification {
  notificationKey: string
  shiftId: string
  shiftDate: string
  shiftEndDateTime: string
  trainNumber: string
  departureDate: string
  fromStation: string
  toStation: string
  taskStartDateTime: string
  taskEndDateTime: string
  delayOccurredAt: string
  kind: PreviousDelayKind
  delayedStation: string
  delayedRowType: 'ARRIVAL' | 'DEPARTURE' | 'UNKNOWN'
}

export interface DelayNotificationsResponse {
  handledKeys: string[]
  pending: PendingDelayNotification[]
}

export interface DelayNotificationState {
  // Key: see getTaskDelayNotificationKey in lib/taskDelayUtils
  // (`${trainNumberNumeric}-${operatingDate}-${fromStation}-${toStation}`)
  notifications: Record<string, DelayNotificationStep>
  // Same key format — notifications that have been fully handled
  handledNotifications: Record<string, boolean>
  // Unhandled notifications from previous shifts (newest first)
  pendingPrevious: PendingDelayNotification[]
  fetchStatus: FetchStatus
}

const initialState: DelayNotificationState = {
  notifications: {},
  handledNotifications: {},
  pendingPrevious: [],
  fetchStatus: 'idle',
}

export const fetchDelayNotificationsAction = createAsyncThunk(
  'delayNotifications/fetch',
  async (_, thunkApi) => {
    try {
      return await apiGET<DelayNotificationsResponse>('/delayNotifications')
    } catch (err: unknown) {
      return thunkApi.rejectWithValue(String(err))
    }
  }
)

export const persistDelayNotificationHandledAction = createAsyncThunk(
  'delayNotifications/persistHandled',
  ({
    notificationKey,
    resolution,
  }: {
    notificationKey: string
    resolution: DelayNotificationResolution
  }) => postDelayNotificationHandled(notificationKey, resolution)
)

const delayNotificationSlice = createSlice({
  name: 'delayNotifications',
  initialState,
  reducers: {
    initNotification: (
      state,
      action: PayloadAction<{ key: string; step?: DelayNotificationStep }>
    ) => {
      const { key, step } = action.payload
      if (!state.notifications[key] && !state.handledNotifications[key]) {
        state.notifications[key] = step ?? 'wasDelayDueToEquipmentFault'
      }
    },
    setNotificationStep: (
      state,
      action: PayloadAction<{ key: string; step: DelayNotificationStep }>
    ) => {
      const { key, step } = action.payload
      state.notifications[key] = step
    },
    addToHandledNotifications: (state, action: PayloadAction<{ key: string }>) => {
      const { key } = action.payload
      state.handledNotifications[key] = true
    },
    markNotificationHandled: (state, action: PayloadAction<{ key: string }>) => {
      const { key } = action.payload
      state.handledNotifications[key] = true
      delete state.notifications[key]
      state.pendingPrevious = state.pendingPrevious.filter(
        (pending) => pending.notificationKey !== key
      )
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDelayNotificationsAction.pending, (state) => {
      state.fetchStatus = 'pending'
    })
    builder.addCase(fetchDelayNotificationsAction.fulfilled, (state, { payload }) => {
      state.fetchStatus = 'fulfilled'
      payload.handledKeys.forEach((key) => {
        state.handledNotifications[key] = true
        delete state.notifications[key]
      })
      const pendingPrevious = payload.pending.filter(
        (pending) => !state.handledNotifications[pending.notificationKey]
      )
      pendingPrevious.forEach((pending) => {
        if (!state.notifications[pending.notificationKey]) {
          state.notifications[pending.notificationKey] = 'previousShiftDelayUnhandled'
        }
      })
      state.pendingPrevious = pendingPrevious
    })
    builder.addCase(fetchDelayNotificationsAction.rejected, (state) => {
      state.fetchStatus = 'failed'
    })
  },
})

const { initNotification } = delayNotificationSlice.actions

export const { setNotificationStep, addToHandledNotifications, markNotificationHandled } =
  delayNotificationSlice.actions

export default delayNotificationSlice.reducer

export const fetchAndInitializeDelayNotifications =
  (shift: Shift): TypedThunk<Promise<void>> =>
  async (dispatch, getState) => {
    if (!selectDelayNotificationsEnabled(getState())) return

    await dispatch(fetchDelayNotificationsAction())

    const state = getState()
    for (const task of shift.tasks ?? []) {
      const vm = buildTaskDelayVM(
        {
          isDriver: state.user.driver,
          punctualityByTrain: state.punctuality.punctualityByTrain,
        },
        task
      )
      if (vm) {
        dispatch(initNotification({ key: vm.notificationKey }))
      }
    }
  }
